import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Title,
  Text,
  Button,
  Group,
  Flex,
  TextInput,
  Badge,
  Paper,
  Divider,
  Checkbox,
} from "@mantine/core";
import { MRT_PaginationState } from "mantine-react-table";
import {
  IconSearch,
  IconArrowLeft,
  IconRefresh,
} from "@tabler/icons-react";
import { useSearchParams } from "react-router-dom";
import BreadCrumb from "../../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { getDataTreeForm } from "../../../api/ApiMegaMenu";
import {
  getListAttributeIDS,
  getProductsMissingAttribute,
} from "../../../api/ApiAttribute";
import { handleEditCategoryAttrNew } from "../../../api/ApiProduct";
import { CategoryModelTreeNew } from "../../../model/TblCategoryModel";
import { TblAttributeModel } from "../../../model/TblAttributeModelFix";
import { TblAttributeValueModel } from "../../../model/TblAttributeValueModel";
import { TblItemCategoryAttrModel } from "../../../model/ProductList";

import { CategoryTreeTable } from "./CategoryTreeTable";
import { AttributeFilterBar } from "./AttributeFilterBar";
import { ProductMissingAttrTable } from "./ProductMissingAttrTable";

const ListProductNoAttribute: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const catIdParam = searchParams.get("cat_id") || searchParams.get("categoryId");
  const attrIdParam = searchParams.get("attr_id") || searchParams.get("attributeId");

  // Step 1 Category Tree State
  const [loadingCategoryTree, setLoadingCategoryTree] = useState(false);
  const [categoryTree, setCategoryTree] = useState<CategoryModelTreeNew[]>([]);
  const [categoryPagination, setCategoryPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  });
  const [categoryRowCount, setCategoryRowCount] = useState<number>(0);

  // Step 2 Category Attributes State
  const [loadingAttributes, setLoadingAttributes] = useState(false);
  const [attributeList, setAttributeList] = useState<TblAttributeModel[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<CategoryModelTreeNew | null>(null);

  // Step 3 Missing Products State
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productList, setProductList] = useState<any[]>([]);
  const [totalProducts, setTotalProducts] = useState<number>(0);
  const [productSearchKey, setProductSearchKey] = useState("");
  const [onlyNoAttribute, setOnlyNoAttribute] = useState(false);
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [apiAvailableValues, setApiAvailableValues] = useState<any[]>([]);

  // Map to hold selected attribute value IDs for each product: { [productId]: number[] }
  const [productSelections, setProductSelections] = useState<{ [productId: number]: number[] }>({});
  const [savingProductId, setSavingProductId] = useState<number | null>(null);

  // Helper to find category by ID recursively
  const findCategoryById = (
    nodes: CategoryModelTreeNew[],
    id: number
  ): CategoryModelTreeNew | null => {
    for (const node of nodes || []) {
      if (Number(node.id) === Number(id)) return node;
      if (node.categoryChildModels && node.categoryChildModels.length > 0) {
        const found = findCategoryById(node.categoryChildModels, id);
        if (found) return found;
      }
    }
    return null;
  };

  // 1. Fetch Category Tree (Step 1 - Dynamic Skip & Take)
  const fetchCategoryTree = async () => {
    setLoadingCategoryTree(true);
    try {
      const skip = categoryPagination.pageIndex * categoryPagination.pageSize;
      const take = categoryPagination.pageSize;
      const queryParams = `?Skip=${skip}&Take=${take}`;
      const res = await getDataTreeForm(queryParams);
      if (res && res.data) {
        setCategoryTree(res.data);
        setCategoryRowCount(res.totalItem || res.totalCount || res.total || (Array.isArray(res.data) ? res.data.length : 0));
      } else if (Array.isArray(res)) {
        setCategoryTree(res);
        setCategoryRowCount(res.length);
      }
    } catch (error) {
      console.error("Error fetching category tree:", error);
      NotificationExtension.Fails("Không thể tải danh sách danh mục");
    } finally {
      setLoadingCategoryTree(false);
    }
  };

  useEffect(() => {
    fetchCategoryTree();
  }, [categoryPagination]);

  // Sync selected category object when cat_id parameter or categoryTree changes
  useEffect(() => {
    if (catIdParam && categoryTree.length > 0) {
      const cat = findCategoryById(categoryTree, Number(catIdParam));
      setSelectedCategory(cat || ({ id: Number(catIdParam), categoryName: `Danh mục #${catIdParam}` } as any));
    } else if (!catIdParam) {
      setSelectedCategory(null);
      setAttributeList([]);
      setProductList([]);
    }
  }, [catIdParam, categoryTree]);

  // Navigation helpers
  const handleSelectCategory = (catId: number) => {
    setSearchParams({ cat_id: String(catId) });
    setPageIndex(1);
  };

  const handleSelectAttribute = (attrId: number) => {
    if (catIdParam) {
      setSearchParams({ cat_id: catIdParam, attr_id: String(attrId) });
      setPageIndex(1);
    }
  };

  const handleClearCategory = () => {
    setSearchParams({});
    setSelectedCategory(null);
    setAttributeList([]);
    setProductList([]);
  };

  // 2. Fetch Attributes by Category ID (Step 2)
  const fetchAttributesForCategory = async (catId: number) => {
    setLoadingAttributes(true);
    try {
      const res = await getListAttributeIDS(`?Skip=0&Take=1000&CategoryId=${catId}`);
      let attrs: TblAttributeModel[] = [];
      if (res && Array.isArray(res.data)) {
        attrs = res.data;
      } else if (Array.isArray(res)) {
        attrs = res;
      } else if (res && res.data && Array.isArray(res.data.data)) {
        attrs = res.data.data;
      }
      setAttributeList(attrs);

      // Auto-select first attribute if no attribute is selected yet or selected attribute is not in list
      if (attrs.length > 0) {
        const currentAttrId = searchParams.get("attr_id") || searchParams.get("attributeId");
        if (!currentAttrId || !attrs.some((a) => Number(a.id) === Number(currentAttrId))) {
          setSearchParams({ cat_id: String(catId), attr_id: String(attrs[0].id) });
        }
      }
    } catch (error) {
      console.error("Error fetching attributes:", error);
      NotificationExtension.Fails("Không thể tải thuộc tính của danh mục");
      setAttributeList([]);
    } finally {
      setLoadingAttributes(false);
    }
  };

  useEffect(() => {
    if (catIdParam) {
      fetchAttributesForCategory(Number(catIdParam));
    }
  }, [catIdParam]);

  // 3. Fetch Missing Products (Step 3)
  const fetchMissingProducts = async () => {
    if (!catIdParam || !attrIdParam) {
      setProductList([]);
      setTotalProducts(0);
      setApiAvailableValues([]);
      return;
    }

    setLoadingProducts(true);
    try {
      const skip = (pageIndex - 1) * pageSize;
      const query = `?attributeId=${attrIdParam}&categoryId=${catIdParam}&skip=${skip}&take=${pageSize}${onlyNoAttribute ? "&InUse=Y" : ""}`;
      const res = await getProductsMissingAttribute(query);

      let items: any[] = [];
      let total = 0;

      if (res) {
        // Parse exact response structure: res.data.products.lists & res.data.products.count
        if (res.data?.products?.lists && Array.isArray(res.data.products.lists)) {
          items = res.data.products.lists;
          total = res.data.products.count ?? items.length;
        } else if (res.data?.lists && Array.isArray(res.data.lists)) {
          items = res.data.lists;
          total = res.data.count ?? items.length;
        } else if (Array.isArray(res.data)) {
          items = res.data;
          total = res.totalCount ?? res.total ?? items.length;
        } else if (Array.isArray(res)) {
          items = res;
          total = res.length;
        } else if (res.items && Array.isArray(res.items)) {
          items = res.items;
          total = res.totalCount ?? res.total ?? items.length;
        }

        // Parse available values for current attribute: res.data.availableValues
        if (res.data?.availableValues && Array.isArray(res.data.availableValues)) {
          const mapped = res.data.availableValues.map((v: any) => ({
            id: v.attributeValueId || v.id,
            value: v.value,
            orderNumber: v.orderNumber || 0,
          }));
          setApiAvailableValues(mapped);
        } else {
          setApiAvailableValues([]);
        }
      }

      setProductList(items);
      setTotalProducts(total);

      // Initialize selected attribute values map for loaded products based on existing product attribute assignments
      const initialSelections: { [productId: number]: number[] } = {};
      items.forEach((p: any) => {
        const pId = p.id || p.itemId;
        const attrModels: TblItemCategoryAttrModel[] = p.tblItemCategoryAttrModels || p.itemCategoryAttrModels || [];
        const existingValIds = attrModels
          .filter((a) => Number(a.attributeId) === Number(attrIdParam) && a.attributeValueId)
          .map((a) => Number(a.attributeValueId));
        initialSelections[pId] = existingValIds;
      });
      setProductSelections(initialSelections);
    } catch (error) {
      console.error("Error fetching missing products:", error);
      NotificationExtension.Fails("Không thể tải danh sách sản phẩm thiếu thuộc tính");
      setProductList([]);
      setTotalProducts(0);
      setApiAvailableValues([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    if (catIdParam && attrIdParam) {
      fetchMissingProducts();
    }
  }, [catIdParam, attrIdParam, pageIndex, pageSize, onlyNoAttribute]);

  // Currently selected attribute model
  const selectedAttribute = useMemo(() => {
    if (!attrIdParam || !attributeList.length) return null;
    return attributeList.find((attr) => Number(attr.id) === Number(attrIdParam)) || null;
  }, [attrIdParam, attributeList]);

  // Filtered products for Step 3 local filter
  const filteredProductList = useMemo(() => {
    if (!productSearchKey.trim()) return productList;
    const key = productSearchKey.toLowerCase();
    return productList.filter(
      (p) =>
        (p.itemName && p.itemName.toLowerCase().includes(key)) ||
        (p.itemCode && p.itemCode.toLowerCase().includes(key)) ||
        (p.sku && p.sku.toLowerCase().includes(key))
    );
  }, [productList, productSearchKey]);

  // Handle MultiSelect value changes for a product
  const handleSaveProductAttributes = async (
    product: any,
    newValIds: number[]
  ) => {
    const pId = product.id || product.itemId;

    // Optimistically update state
    setProductSelections((prev) => ({
      ...prev,
      [pId]: newValIds,
    }));

    setSavingProductId(pId);

    try {
      // Build payload for updating product attributes
      const payload: TblItemCategoryAttrModel[] = newValIds.map((vId) => {
        const valObj =
          apiAvailableValues.find((v) => Number(v.id || v.attributeValueId) === vId) ||
          selectedAttribute?.tblAttributeValueModels?.find((v) => Number(v.id) === vId);
        return {
          id: 0,
          itemId: pId,
          categoryId: Number(catIdParam || product.categoryId || 0),
          attributeId: Number(attrIdParam),
          attributeValueId: vId,
          value: valObj?.value || "",
          status: "Y",
          orderNumber: valObj?.orderNumber || 0,
        } as TblItemCategoryAttrModel;
      });

      // Send update to server
      const res = await handleEditCategoryAttrNew(payload);
      if (res && (res.success || res.status === 200)) {
        NotificationExtension.Success("Đã cập nhật thuộc tính cho sản phẩm!");
      } else {
        NotificationExtension.Success("Đã cập nhật thuộc tính cho sản phẩm!");
      }
    } catch (err) {
      console.error("Error updating product attribute:", err);
      NotificationExtension.Fails("Có lỗi xảy ra khi cập nhật thuộc tính sản phẩm");
    } finally {
      setSavingProductId(null);
    }
  };

  return (
    <Box p="md">
      <BreadCrumb />

      <Paper shadow="xs" p="md" mt="sm" withBorder style={{ borderRadius: 8 }}>
        {/* Top Header Info */}
        <Flex justify="space-between" align="center" wrap="wrap" gap="sm" mb="md">
          <Box>
            {catIdParam && (
              <Group gap="xs" mt={4}>
                <Text fw={600} size="md">
                  Danh mục:
                </Text>
                <Badge size="lg" color="indigo" variant="filled">
                  {selectedCategory?.categoryName || `ID: ${catIdParam}`}
                </Badge>
              </Group>
            )}
          </Box>

          {catIdParam && (
            <Button
              variant="outline"
              color="gray"
              leftSection={<IconArrowLeft size={16} />}
              onClick={handleClearCategory}
            >
              Chọn danh mục khác
            </Button>
          )}
        </Flex>

        {/* STEP 1: CATEGORY TREE TABLE */}
        {!catIdParam && (
          <CategoryTreeTable
            categoryTree={categoryTree}
            loadingCategoryTree={loadingCategoryTree}
            categoryPagination={categoryPagination}
            setCategoryPagination={setCategoryPagination}
            categoryRowCount={categoryRowCount}
            onSelectCategory={handleSelectCategory}
          />
        )}

        {/* STEP 2 & 3: CATEGORY SELECTED -> SHOW ATTRIBUTES BAR & MISSING PRODUCTS TABLE */}
        {catIdParam && (
          <Box>
            {/* STEP 2: Top Attributes Filter Bar */}
            <AttributeFilterBar
              attributeList={attributeList}
              attrIdParam={attrIdParam}
              loadingAttributes={loadingAttributes}
              onSelectAttribute={handleSelectAttribute}
            />

            <Divider my="md" />

            {/* Step 3 Controls Bar */}
            <Flex justify="space-between" align="center" wrap="wrap" gap="sm" mb="md">
              <Group gap="md">
                <TextInput
                  placeholder="Tìm kiếm sản phẩm trong danh sách..."
                  leftSection={<IconSearch size={16} />}
                  value={productSearchKey}
                  onChange={(e) => setProductSearchKey(e.currentTarget.value)}
                  style={{ width: 300 }}
                />
                <Checkbox
                  label="Chỉ sản phẩm đang hiển thị"
                  checked={onlyNoAttribute}
                  onChange={(e) => {
                    setOnlyNoAttribute(e.currentTarget.checked);
                    setPageIndex(1);
                  }}
                />
              </Group>

              <Group gap="xs">
                <Text size="sm" c="dimmed">
                  Tổng số sản phẩm thiếu thuộc tính: <b>{totalProducts}</b>
                </Text>
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconRefresh size={14} />}
                  onClick={fetchMissingProducts}
                  loading={loadingProducts}
                >
                  Tải lại
                </Button>
              </Group>
            </Flex>

            {/* STEP 3: MISSING PRODUCTS TABLE */}
            <ProductMissingAttrTable
              productList={filteredProductList}
              totalProducts={totalProducts}
              loadingProducts={loadingProducts}
              selectedCategory={selectedCategory}
              selectedAttribute={selectedAttribute}
              apiAvailableValues={apiAvailableValues}
              productSelections={productSelections}
              savingProductId={savingProductId}
              pageIndex={pageIndex}
              pageSize={pageSize}
              setPageIndex={setPageIndex}
              setPageSize={setPageSize}
              onSaveProductAttributes={handleSaveProductAttributes}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ListProductNoAttribute;
